import { useState } from 'react';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { Card, CardHeader } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { useLabelMappings, useLabelMappingActions } from '@/hooks/useDispatcher';
import { useCurrentWorkspace } from '@/lib/workspace-context';
import type { LabelMapping } from '@/types/api';
import { LabelMappingFormModal } from './LabelMappingFormModal';

const EVENT_LABELS: Record<string, string> = {
  bounced: 'Bounced',
  unsubscribed: 'Unsubscribed',
  qualified: 'Qualified',
  not_interested: 'Not interested',
};

const PROVIDER_LABELS: Record<string, string> = {
  successai: 'Success.ai',
  instantly: 'Instantly',
};

export default function LabelMappingsPage() {
  const { currentWorkspace } = useCurrentWorkspace();
  const workspaceId = currentWorkspace?.id ?? '';

  const { data, isLoading } = useLabelMappings({ workspace_id: workspaceId });
  const { remove } = useLabelMappingActions(workspaceId);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<LabelMapping | null>(null);

  function openCreate() {
    setEditing(null);
    setModalOpen(true);
  }

  function openEdit(mapping: LabelMapping) {
    setEditing(mapping);
    setModalOpen(true);
  }

  async function handleDelete(mapping: LabelMapping) {
    const name = mapping.successai_label_name ?? mapping.successai_label_id;
    if (!confirm(`Supprimer le mapping "${name}" ?`)) return;
    try {
      await remove.mutateAsync(mapping.id);
      toast.success('Mapping supprimé');
    } catch {
      toast.error('Échec de la suppression');
    }
  }

  return (
    <div>
      <div className="flex justify-end mb-4">
        <Button onClick={openCreate}>
          <Plus size={14} /> Ajouter un mapping
        </Button>
      </div>

      <Card>
        <CardHeader
          title="Mappings de labels"
          subtitle="Mappe des labels Success.ai / Instantly vers des événements Dispatcher"
        />

        {isLoading ? (
          <div className="p-5">
            <Skeleton className="h-32 w-full" />
          </div>
        ) : !data?.items.length ? (
          <EmptyState title="Aucun mapping configuré" />
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-brand-border">
              <tr>
                <th className="th">Nom du label</th>
                <th className="th">Label ID</th>
                <th className="th">Provider</th>
                <th className="th">Événement mappé</th>
                <th className="th">Actif</th>
                <th className="th">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-brand-border">
              {data.items.map((m) => (
                <tr key={m.id}>
                  <td className="td font-medium text-brand-dark">
                    {m.successai_label_name ?? '—'}
                  </td>
                  <td className="td font-mono text-xs">{m.successai_label_id}</td>
                  <td className="td">
                    <span className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-700">
                      {PROVIDER_LABELS[m.provider] ?? m.provider}
                    </span>
                  </td>
                  <td className="td">
                    <span className="text-xs px-2 py-0.5 rounded bg-blue-50 text-blue-700">
                      {EVENT_LABELS[m.mapped_event] ?? m.mapped_event}
                    </span>
                  </td>
                  <td className="td">
                    <span
                      className={`text-xs px-2 py-0.5 rounded ${
                        m.active
                          ? 'bg-green-50 text-green-700'
                          : 'bg-gray-100 text-gray-500'
                      }`}
                    >
                      {m.active ? 'Oui' : 'Non'}
                    </span>
                  </td>
                  <td className="td">
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => openEdit(m)}>
                        <Pencil size={12} />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => handleDelete(m)}>
                        <Trash2 size={12} />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <LabelMappingFormModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        workspaceId={workspaceId}
        editing={editing}
      />
    </div>
  );
}
